import { ElMessage } from 'element-plus';
import { defineStore } from 'pinia';
import { watch } from 'vue';

import { client, getErrorMessage } from '@/api';
import {
  CollectionGameDTO,
  CollectionGameModification,
  PaginatedCollectionGamesResponse,
} from '@/api/models';
import { HotColdCollection } from '@/api/response/collections/HotColdCollection';
import { debounce } from 'es-toolkit';
import { useCollectionsStore } from '@/store/collections';

export type HotColdCollectionsState = {
  isWatching: boolean;
  loading: boolean;
  error: string | null;
  search: string | null;

  localeId: number;
  collection: HotColdCollection | null;
  game: CollectionGameDTO | null;
  games: CollectionGameDTO[];
  collections: HotColdCollection[];

  isAddGameOpened: boolean;
  isEditGameOpened: boolean;

  // There is a need additional implementation in catalog
  // to support sorting
  sortName: 'id' | 'rtp' | null;
  sortDirection: 'ASC' | 'DESC' | null;

  pagination: {
    page: number;
    pageSize: number;
    total: number;
    pageCount: number;
  };
};

const DEFAULT_PAGINATION = {
  page: 1,
  perPage: 15,
  total: 0,
  pageSize: 15,
  pageCount: 0,
};

export const useHotColdCollectionsStore = defineStore('hotColdCollections', {
  state: (): HotColdCollectionsState => ({
    isWatching: false,
    loading: false,
    error: null,
    search: null,
    localeId: 1, // catalog does not have default locale them 1 locale will be set as default
    collection: null,
    game: null,
    collections: [],
    isAddGameOpened: false,
    isEditGameOpened: false,
    sortName: null,
    sortDirection: null,
    games: [],
    pagination: { ...DEFAULT_PAGINATION },
  }),

  actions: {
    init() {
      const debounceFetchCollections = debounce(() => {
        if (this.isWatching) {
          this.refreshCollections();
        }
      }, 300);

      watch(
        () => [this.localeId],
        () => debounceFetchCollections(),
        { immediate: true },
      );

      const debounceFetchGames = debounce(() => {
        if (this.isWatching) {
          this.refreshGames();
        }
      }, 300);

      watch(
        () => [this.search, this.collection, this.sortName, this.sortDirection],
        () => debounceFetchGames(),
        { immediate: true },
      );
    },

    reset() {
      this.$reset();
    },

    async refreshCollections() {
      this.games = [];
      this.pagination = { ...DEFAULT_PAGINATION };

      await this.getHotColdCollections({ localeId: this.localeId });
    },

    async refreshGames() {
      await this.getCollectionGames();
    },

    setPagination({ page, limit }: { page: number; limit: number }): void {
      this.pagination = {
        ...this.pagination,
        page,
        pageSize: limit,
      };

      this.refreshGames();
    },

    setLoading(value: boolean) {
      this.loading = value;
    },

    setError(value: string | null) {
      this.error = value;
    },

    setLocaleId(localeId: number) {
      this.localeId = localeId;
    },

    setSort({ prop, order }: { prop: string; order: string | null }): void {
      if (order === null) {
        this.sortDirection = null;
        this.sortName = null;
      } else {
        this.sortName = prop as 'rtp';
        this.sortDirection = order === 'ascending' ? 'ASC' : 'DESC';
      }
    },

    openAddGameModal() {
      this.isAddGameOpened = true;
    },
    closeAddGameModal() {
      this.isAddGameOpened = false;
    },

    openEditGameModal() {
      this.isEditGameOpened = true;
    },
    closeEditGameModal() {
      this.isEditGameOpened = false;
    },

    getMaxPosition(): number {
      let max: number = 0;

      for (const game of this.games) {
        if (typeof game.position === 'number' && game.position > max) {
          max = game.position;
        }
      }

      return max + 1;
    },

    async addGame(gameSlug: string, rtp: number) {
      const isSuccess = await this.updateCurrentCollection({
        gameSlug,
        rtp,
        position: this.getMaxPosition(),
        modificationType: 'INSERT',
        isManual: true,
      });

      if (isSuccess) {
        await this.refreshGames();
      }
    },

    async removeGame(item: CollectionGameDTO) {
      const isSuccess = await this.updateCurrentCollection({
        gameSlug: item.gameSlug,
        modificationType: 'DELETE',
        isManual: true,
      });

      if (isSuccess) {
        await this.refreshGames();
      }
    },

    async setPosition(item: CollectionGameDTO, position: number | undefined) {
      const isSuccess = await this.updateCurrentCollection({
        gameSlug: item.gameSlug,
        position,
        modificationType: 'UPDATE',
        isManual: true,
      });

      if (isSuccess) {
        await this.refreshGames();
      }
    },

    async updateGame(item: CollectionGameDTO, position: number, rtp: number) {
      const isSuccess = await this.updateCurrentCollection({
        gameSlug: item.gameSlug,
        rtp: rtp > 0 ? rtp : undefined,
        position: position > 0 ? position : undefined,
        modificationType: 'UPDATE',
        isManual: true,
      });

      if (isSuccess) {
        await this.refreshGames();
      }
    },

    async updateCurrentCollection(data: CollectionGameModification) {
      if (!this.collection) {
        ElMessage.info('Category is not selected');
        return;
      }

      const collectionStore = useCollectionsStore();

      const collection = await collectionStore.updateCollection(
        this.collection.collectionId,
        { games: [data] },
      );

      return Boolean(collection);
    },

    async getCollectionGames() {
      if (this.collection === null) {
        // Do nothing while getting list of collections
        return;
      }

      this.setError(null);
      this.setLoading(true);

      let data: PaginatedCollectionGamesResponse = {
        items: [],
        page: 1,
        pageSize: 15,
        total: 0,
        pageCount: 0,
      };

      try {
        data = await client.collectionsApi.listCollectionGames(
          this.collection.collectionId,
          {
            search: this.search ?? undefined,
            pageNumber: this.pagination.page,
            pageSize: this.pagination.pageSize,
          },
        );

        const { items = [], total, page, pageSize, pageCount } = data;

        this.games = items;

        this.pagination = {
          page,
          pageSize,
          total,
          pageCount,
        };
      } catch (err) {
        const message = getErrorMessage(err, 'Error loading collection games');

        ElMessage.error(message);

        this.setError(message);
      } finally {
        this.setLoading(false);
      }

      return data;
    },

    async getHotColdCollections(query: { localeId: number }) {
      this.setError(null);
      this.setLoading(true);

      let data: HotColdCollection[] = [];

      try {
        data = await client.collectionsApi.getHotColdCollections(query);

        this.collections = data;
        this.collection = data[0] ?? null;
      } catch (err) {
        const message = getErrorMessage(
          err,
          'Error loading hot and cold collections',
        );

        ElMessage.error(message);

        this.setError(message);
      } finally {
        this.setLoading(false);
      }

      return data;
    },
  },
});
